import axios from 'axios'
import { createStore } from 'zustand/vanilla'
import { appActions } from './app'

/**
 * The signed-in user: token, tier, workspace, the server's feature list, the
 * upgrade prompt. Ported verbatim from the Pinia options store (Phase 1B) —
 * same three localStorage keys (auth_token, workspace_id, user_tier), which
 * the router guard and bootstrapSession also read by name.
 */
export type Feature = { name: string; unlocked: boolean; category: string; description: string }
export type UserState = {
  isAuthenticated: boolean
  token: string | null
  email: string | null
  tier: string
  subscriptionStatus: string | null
  trialDaysLeft: number
  trialEndDate: string | null
  workspaceId: number
  workspaceName: string | null
  features: Record<string, Feature>
  showUpgradeModal: boolean
  upgradeModalFeature: string | null
  upgradeModalTier: string | null
  loading: boolean
  error: string | null
}

// Relative by default, like auth.js and client.js: the Vite dev server proxies /api.
const API_BASE = import.meta.env.VITE_API_URL || ''
const PAID = ['essential', 'professional', 'enterprise']

function initialState(): UserState {
  const savedWorkspaceIdRaw = localStorage.getItem('workspace_id')
  return {
    isAuthenticated: false,
    token: localStorage.getItem('auth_token') || null,
    email: null,
    tier: localStorage.getItem('user_tier') || 'demo',
    subscriptionStatus: null,
    trialDaysLeft: 0,
    trialEndDate: null,
    workspaceId: savedWorkspaceIdRaw !== null ? parseInt(savedWorkspaceIdRaw, 10) || 0 : 0,
    workspaceName: null,
    features: {},
    showUpgradeModal: false,
    upgradeModalFeature: null,
    upgradeModalTier: null,
    loading: false,
    error: null,
  }
}

export const userStore = createStore<UserState>(() => initialState())
const get = () => userStore.getState()
const set = (partial: Partial<UserState>) => userStore.setState(partial)

export const userSelectors = {
  userEmail: (s: UserState) => s.email,
  isDemo: (s: UserState) => s.tier === 'demo',
  isDemoUser: (s: UserState) => s.tier === 'demo',
  isEssentialUser: (s: UserState) => PAID.includes(s.tier),
  isProfessionalUser: (s: UserState) => ['professional', 'enterprise'].includes(s.tier),
  isEnterpriseUser: (s: UserState) => s.tier === 'enterprise',
  isOnTrial: (s: UserState) => s.subscriptionStatus === 'trial',
  trialExpiringSoon: (s: UserState) => s.trialDaysLeft <= 7 && s.trialDaysLeft > 0,
  trialExpired: (s: UserState) => s.trialDaysLeft === 0 && s.subscriptionStatus === 'trial',
  canUseFeature: (s: UserState) => (featureName: string) => {
    const feature = s.features[featureName]
    return feature ? feature.unlocked : false
  },
  canRunOptimization: (s: UserState) => PAID.includes(s.tier),
  canUseTimePeriods: (s: UserState) => ['professional', 'enterprise'].includes(s.tier),
  canUseUncertainty: (s: UserState) => s.tier === 'enterprise',
  canRunSimulation: (s: UserState) => s.tier === 'enterprise',
  canExportReports: (s: UserState) => PAID.includes(s.tier),
  canImportExcel: (s: UserState) => PAID.includes(s.tier),
  canUseAIChat: (s: UserState) => PAID.includes(s.tier),
  canEditData: (s: UserState) => PAID.includes(s.tier),
  canSaveScenarios: (s: UserState) => PAID.includes(s.tier),
}

type AuthUser = { email: string; tier: string; trial_days_left: number; workspace_id: number; subscription_status?: string }

function persistSession(token: string, workspaceId: number, tier: string) {
  localStorage.setItem('auth_token', token)
  localStorage.setItem('workspace_id', String(workspaceId))
  localStorage.setItem('user_tier', tier)
}

async function establish(response: { data: { token: string; user: AuthUser } }, subscriptionStatus: string | null) {
  const u = response.data.user
  set({
    token: response.data.token,
    email: u.email,
    tier: u.tier,
    subscriptionStatus,
    trialDaysLeft: u.trial_days_left,
    workspaceId: u.workspace_id,
    isAuthenticated: true,
  })
  persistSession(response.data.token, u.workspace_id, u.tier)
  await userActions.loadFeatures()
  if (get().tier !== 'demo') userActions.clearDemoParams()
  userActions.syncAppStoreState()
}

export const userActions = {
  async init() {
    if (get().token) {
      await userActions.validateToken()
      await userActions.loadFeatures()
    }
    userActions.syncAppStoreState()
  },
  async signup(email: string) {
    set({ loading: true, error: null })
    try {
      const response = await axios.post(`${API_BASE}/api/auth/signup`, { email })
      await establish(response, 'trial')
      return { success: true }
    } catch (error) {
      const message = (error as { response?: { data?: { error?: string } } }).response?.data?.error || 'Signup failed'
      set({ error: message })
      return { success: false, error: message }
    } finally {
      set({ loading: false })
    }
  },
  async signupWithPassword(email: string, password: string) {
    set({ loading: true, error: null })
    try {
      const response = await axios.post(`${API_BASE}/api/auth/signup-password`, { email, password })
      await establish(response, 'trial')
      return { success: true }
    } catch (error) {
      const message = (error as { response?: { data?: { error?: string } } }).response?.data?.error || 'Signup failed'
      set({ error: message })
      return { success: false, error: message }
    } finally {
      set({ loading: false })
    }
  },
  async login(email: string, password: string) {
    set({ loading: true, error: null })
    try {
      const response = await axios.post(`${API_BASE}/api/auth/login`, { email, password })
      await establish(response, response.data.user.subscription_status ?? null)
      return { success: true }
    } catch (error) {
      const message = (error as { response?: { data?: { error?: string } } }).response?.data?.error || 'Login failed'
      set({ error: message })
      return { success: false, error: message }
    } finally {
      set({ loading: false })
    }
  },
  async validateToken() {
    const token = get().token
    if (!token) return false
    try {
      const response = await axios.get(`${API_BASE}/api/auth/validate`, { headers: { Authorization: `Bearer ${token}` } })
      if (response.data.authenticated) {
        const next: Partial<UserState> = {
          email: response.data.email,
          tier: response.data.tier,
          subscriptionStatus: response.data.subscription_status,
          trialDaysLeft: response.data.trial_days_left,
          isAuthenticated: true,
        }
        if (response.data.workspace_id !== undefined && response.data.workspace_id !== null) {
          next.workspaceId = response.data.workspace_id
          localStorage.setItem('workspace_id', String(response.data.workspace_id))
        }
        set(next)
        localStorage.setItem('user_tier', response.data.tier)
        if (get().tier !== 'demo') userActions.clearDemoParams()
        userActions.syncAppStoreState()
        return true
      }
      set({ tier: 'demo', workspaceId: 0, isAuthenticated: false })
      userActions.syncAppStoreState()
      return false
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status
      if (status === 401 || status === 403) await userActions.logout()
      return false
    }
  },
  async loadFeatures() {
    try {
      const token = get().token
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const response = await axios.get(`${API_BASE}/api/auth/features`, { headers })
      const features: Record<string, Feature> = {}
      for (const feature of response.data.features as Feature[]) features[feature.name] = feature
      const next: Partial<UserState> = { features, tier: response.data.tier }
      if (response.data.workspace_id !== undefined && response.data.workspace_id !== null) next.workspaceId = response.data.workspace_id
      set(next)
      if (get().tier !== 'demo') userActions.clearDemoParams()
      userActions.syncAppStoreState()
    } catch {
      userActions.syncAppStoreState()
    }
  },
  async logout() {
    const token = get().token
    if (token) {
      try {
        await axios.post(`${API_BASE}/api/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } })
      } catch {
        /* the session is cleared locally regardless */
      }
    }
    set({ token: null, isAuthenticated: false, email: null, tier: 'demo', subscriptionStatus: null, trialDaysLeft: 0, workspaceId: 0, features: {} })
    localStorage.removeItem('auth_token')
    localStorage.removeItem('workspace_id')
    localStorage.removeItem('user_tier')
    userActions.syncAppStoreState()
  },
  setTier(tier: string) {
    set({ tier })
    localStorage.setItem('user_tier', tier)
    if (tier !== 'demo') userActions.clearDemoParams()
    userActions.syncAppStoreState()
  },
  setWorkspaceId(id: number | string) {
    const parsed = typeof id === 'string' ? parseInt(id, 10) : id
    const normalized = Number.isFinite(parsed) ? parsed : 0
    set({ workspaceId: normalized })
    localStorage.setItem('workspace_id', String(normalized))
    userActions.syncAppStoreState()
  },
  setDemoMode(isDemoMode: boolean) {
    const tier = isDemoMode ? 'demo' : 'enterprise'
    if (isDemoMode) {
      set({ tier, workspaceId: 0 })
      localStorage.setItem('user_tier', 'demo')
      localStorage.removeItem('workspace_id')
    } else {
      const workspaceId = get().workspaceId || 0
      set({ tier, workspaceId })
      localStorage.setItem('user_tier', tier)
      if (workspaceId) localStorage.setItem('workspace_id', String(workspaceId))
    }
    if (!isDemoMode) userActions.clearDemoParams()
    userActions.syncAppStoreState()
  },
  /** The app store's tier and workspace follow the user's; a demo tier inside a real workspace counts as a trial. */
  syncAppStoreState() {
    const s = get()
    const effectiveTier = s.workspaceId > 0 && s.tier === 'demo' ? 'trial' : s.tier || 'demo'
    appActions.setSubscriptionTier(effectiveTier)
    appActions.setWorkspaceId(s.workspaceId)
  },
  clearDemoParams() {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    let changed = false
    if (url.searchParams.has('demo')) {
      url.searchParams.delete('demo')
      changed = true
    }
    if (url.searchParams.has('tier')) {
      url.searchParams.delete('tier')
      changed = true
    }
    if (changed) window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`)
  },
  checkFeatureAccess(featureName: string) {
    const feature = get().features[featureName]
    if (!feature || !feature.unlocked) {
      let requiredTier = 'essential'
      if (featureName.includes('time_period') || featureName.includes('seasonal')) requiredTier = 'professional'
      else if (featureName.includes('simulation') || featureName.includes('uncertainty') || featureName.includes('risk')) requiredTier = 'enterprise'
      userActions.showUpgradePrompt(featureName, requiredTier)
      return false
    }
    return true
  },
  showUpgradePrompt(featureName: string, requiredTier: string) {
    set({ showUpgradeModal: true, upgradeModalFeature: featureName, upgradeModalTier: requiredTier })
  },
  closeUpgradeModal() {
    set({ showUpgradeModal: false, upgradeModalFeature: null, upgradeModalTier: null })
  },
}

export function __resetUserForTests() {
  userStore.setState(initialState(), true)
}
